<template>
  <div class="home">
    <div>
      <div class="row" :class="editorMode ? 'mb-0' : 'mb-4'">
        <div class="col">
          <div v-if="editorMode" class="editor-bar">
            <span class="editor-bar-title">{{
              $t("dashboardBlocks.editor.mode")
            }}</span>
            <SegmentedControl
              v-model="viewId"
              :options="viewOptions"
              :aria-label="$t('dashboardBlocks.editor.views')"
            />
            <input
              v-if="dashboard"
              v-model="dashboard.name"
              class="form-control form-control-sm editor-bar-name"
              :aria-label="$t('dashboardBlocks.editor.viewName')"
              @input="dirty = true"
            />
            <div class="dropdown">
              <button
                type="button"
                class="btn btn-secondary btn-small dropdown-toggle"
                data-bs-toggle="dropdown"
                data-bs-auto-close="outside"
                aria-expanded="false"
              >
                {{
                  $t("dashboardBlocks.editor.visibleFor", {
                    who: visibleSummary,
                  })
                }}
              </button>
              <div class="dropdown-menu p-2">
                <label class="cstm-check dropdown-item">
                  <span class="text-black mx-2 regular-14">{{
                    $t("lineOptions.allGroups")
                  }}</span>
                  <input
                    type="checkbox"
                    v-model="visible"
                    :value="0"
                    v-on:change="changeVisible(0)"
                  /><span class="checkmark mx-2"></span>
                </label>
                <label
                  class="cstm-check dropdown-item"
                  v-for="role in roles"
                  :key="role.id"
                >
                  <span class="text-black mx-2 regular-14">{{
                    role.name
                  }}</span>
                  <input
                    type="checkbox"
                    v-model="visible"
                    :value="role.id"
                    v-on:change="changeVisible(role.id)"
                  /><span class="checkmark mx-2"></span>
                </label>
              </div>
            </div>
            <span v-if="dirty" class="editor-bar-dirty" role="status">{{
              $t("dashboardBlocks.editor.unsaved")
            }}</span>
            <span class="me-auto"></span>
            <button class="btn btn-secondary btn-small" @click="exitEditor">
              {{ $t("dashboardBlocks.editor.exit") }}
            </button>
            <button
              class="btn btn-primary btn-small"
              :class="{ 'loading-spin': saving }"
              :disabled="saving"
              @click="saveLayout"
            >
              <span>{{ $t("dashboardBlocks.editor.save") }}</span>
            </button>
          </div>
          <template v-else-if="dashboards.length > 1">
            <button
              v-for="(d, i) in dashboards"
              :key="d.id"
              :class="{
                'btn-no-l': i === dashboards.length - 1,
                'btn-no-r': i === 0,
                'btn-secondary': dashboard.id !== d.id,
                'btn-primary': dashboard.id === d.id,
              }"
              class="btn btn-small"
              @click="dashboard = d"
            >
              {{ d.name }}
            </button>
          </template>
        </div>
        <div class="col-auto" v-if="isAdmin && !editorMode">
          <button class="btn btn-type-4 btn-small" @click="editorMode = true">
            <span>{{ $t("dashboardBlocks.editor.edit") }}</span>
          </button>
        </div>
      </div>
    </div>
    <div class="row" :class="{ 'editor-mode': editorMode }">
      <draggable
        v-model="panels"
        item-key="id"
        class="dashboard-grid"
        :disabled="!editorMode"
        handle=".drag-handle"
        @end="dirty = true"
      >
        <template #item="{ element: panel }">
          <div
            class="dashboard-item"
            :class="{ selected: editorMode && selectedId === panel.id }"
            :style="gridStyle(panel)"
            @click="editorMode && (selectedId = panel.id)"
          >
            <div
              v-if="editorMode && selectedId === panel.id"
              class="block-toolbar"
              role="toolbar"
              :aria-label="panelLabel(panel)"
              @click.stop
            >
              <span class="block-toolbar-label">{{
                $t("dashboardBlocks.editor.width")
              }}</span>
              <div
                class="block-toolbar-sizes"
                role="radiogroup"
                :aria-label="$t('dashboardBlocks.editor.width')"
              >
                <button
                  v-for="option in sizeOptions"
                  :key="option.value"
                  type="button"
                  role="radio"
                  :aria-checked="panel.size === option.value ? 'true' : 'false'"
                  :class="{ active: panel.size === option.value }"
                  @click="setSize(panel, option.value)"
                >
                  {{ option.label }}
                </button>
              </div>
              <template v-if="panel.custom">
                <span class="block-toolbar-separator" aria-hidden="true"></span>
                <button
                  type="button"
                  @click="$refs.blockWizard.open(panel.data.block)"
                >
                  ✎ {{ $t("dashboardBlocks.editor.change") }}
                </button>
              </template>
              <span class="block-toolbar-separator" aria-hidden="true"></span>
              <button
                type="button"
                class="block-toolbar-remove"
                @click="removePanel(panel)"
              >
                ✕ {{ $t("dashboardBlocks.editor.remove") }}
              </button>
            </div>
            <div class="white-box" :class="panel?.class">
              <div class="white-box-top">
                <span
                  v-if="editorMode"
                  class="drag-handle"
                  :aria-label="$t('dashboardBlocks.editor.move')"
                  >⋮⋮</span
                >
              </div>
              <div class="p-1">
                <component
                  :is="panel.component"
                  v-model="panel.data"
                  :appId="panel.id"
                  :key="panel.id + '-' + key"
                  @loaded="$nextTick(calculateHeights)"
                />
              </div>
              <span
                v-if="editorMode && selectedId === panel.id"
                class="resize-handle"
                role="slider"
                tabindex="0"
                :aria-label="$t('dashboardBlocks.editor.resize')"
                aria-valuemin="1"
                aria-valuemax="12"
                :aria-valuenow="panel.size || 6"
                @pointerdown.prevent="startResize($event, panel)"
                @keydown.left.prevent="setSize(panel, (panel.size || 6) - 1)"
                @keydown.right.prevent="setSize(panel, (panel.size || 6) + 1)"
              ></span>
            </div>
          </div>
        </template>
      </draggable>
    </div>
    <div
      v-if="editorMode"
      class="editor-dock"
      role="region"
      :aria-label="$t('dashboardBlocks.editor.library')"
    >
      <strong class="editor-dock-title">{{
        $t("dashboardBlocks.editor.library")
      }}</strong>
      <div class="editor-dock-cards">
        <button
          type="button"
          class="dock-card dock-card-new"
          @click="$refs.blockWizard.open()"
        >
          <BaseIcon name="ai" />
          <strong>{{ $t("dashboardBlocks.wizard.title") }}</strong>
        </button>
        <button
          v-for="panel in availablePanels"
          :key="panel.id"
          type="button"
          class="dock-card"
          :aria-label="
            $t('dashboardBlocks.editor.add', { name: panelLabel(panel) })
          "
          @click="addPanel(panel)"
        >
          <span class="dock-card-name">{{ panelLabel(panel) }}</span>
          <span class="dock-card-foot">
            {{
              panel.custom
                ? $t("dashboardBlocks.editor.customTag")
                : $t("dashboardBlocks.editor.basicTag")
            }}
            <span class="dock-card-plus" aria-hidden="true">+</span>
          </span>
        </button>
        <span
          v-if="availablePanels.length === 0"
          class="text-muted regular-14 align-self-center"
          >{{ $t("dashboardBlocks.editor.allAdded") }}</span
        >
      </div>
    </div>
    <DashboardBlockWizard
      ref="blockWizard"
      @saved="blockSaved"
      @delete="askDeleteBlock"
    />
    <ConfirmModal
      ref="discardModal"
      :title="$t('dashboardBlocks.editor.discardTitle')"
      :text="$t('dashboardBlocks.editor.discardText')"
      :cancel-button="$t('dashboardBlocks.delete.cancel')"
      :ok-button="$t('dashboardBlocks.editor.discardOk')"
      v-on:success="discard"
    />
    <ConfirmModal
      ref="deleteBlockModal"
      :title="$t('dashboardBlocks.delete.title')"
      :text="$t('dashboardBlocks.delete.text', { name: blockToDelete?.name })"
      :cancel-button="$t('dashboardBlocks.delete.cancel')"
      :ok-button="$t('dashboardBlocks.delete.ok')"
      v-on:success="deleteBlock"
    />
  </div>
</template>

<script>
import { useStore } from "vuex";
import externalComponent from "../../modules/externalComponent";
import http from "../../modules/http";
import EditOnClick from "../../components/inputs/EditOnClick";
import { LineChart } from "vue-chart-3";
import draggable from "vuedraggable";
import { defineAsyncComponent } from "vue";
const BaseIcon = defineAsyncComponent(() =>
  import("../../components/icons/BaseIcon.vue")
);
import DashboardBlockWizard from "../../components/modals/DashboardBlockWizard.vue";
import ConfirmModal from "../../components/modals/ConfirmModal";
import SegmentedControl from "../../components/inputs/SegmentedControl.vue";

export default {
  name: "Home",
  components: {
    EditOnClick,
    LineChart,
    draggable,
    DashboardBlockWizard,
    ConfirmModal,
    SegmentedControl,
    BaseIcon,
  },
  data() {
    return {
      store: useStore(),
      visible: [0],
      roles: [],
      isAdmin: false,
      allDashboards: [],
      dashboards: [],
      dashboard: null,
      editedName: "",
      panels: [],
      possiblePanels: {},
      appData: {},
      total: {},
      payments: [],
      switch2: "category",
      key: 0,
      editorMode: false,
      firstLoad: true,
      blockToDelete: null,
      selectedId: null,
      dirty: false,
      saving: false,
      optionsNoText: {
        padding: 0,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            display: false,
            grid: {
              display: false,
            },
          },
          y: {
            display: false,
            grid: {
              display: false,
            },
          },
        },
      },
      options: {
        padding: 0,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            display: true,
            grid: {
              display: false,
            },
          },
          y: {
            display: false,
            grid: {
              display: false,
            },
          },
        },
      },
      revenueByDays: {
        labels: [],
        datasets: [
          {
            label: "Sold Products",
            data: [],
            lineTension: 0.45,
            borderColor: "rgba(160,85,224,1)",
            backgroundColor: "rgba(160,85,224,0.3)",
            fill: true,
          },
        ],
      },
      salesByDays: {
        labels: [],
        datasets: [
          {
            label: "Sales",
            data: [],
            lineTension: 0.45,
            borderColor: "rgba(160,85,224,1)",
            backgroundColor: "rgba(160,85,224,0.3)",
            fill: true,
          },
        ],
      },
      avgByDays: {
        labels: [],
        datasets: [
          {
            label: "Sales",
            data: [],
            lineTension: 0.45,
            borderColor: "rgba(160,85,224,1)",
            backgroundColor: "rgba(160,85,224,0.3)",
            fill: true,
          },
        ],
      },
    };
  },
  computed: {
    sizeOptions() {
      return [
        { value: 3, label: "¼" },
        { value: 4, label: "⅓" },
        { value: 6, label: "½" },
        { value: 8, label: "⅔" },
        { value: 12, label: "1/1" },
      ];
    },
    viewOptions() {
      return this.allDashboards.map((d) => ({
        value: d.id ?? "new",
        label: d.isNew ? this.$t("dashboardBlocks.editor.newView") : d.name,
      }));
    },
    viewId: {
      get() {
        return this.dashboard?.id ?? "new";
      },
      set(value) {
        const view = this.allDashboards.find((d) => (d.id ?? "new") === value);
        if (view) this.dashboard = view;
      },
    },
    visibleSummary() {
      if (this.visible.includes(0)) return this.$t("lineOptions.allGroups");
      return (
        this.roles
          .filter((role) => this.visible.includes(role.id))
          .map((role) => role.name)
          .join(", ") || "–"
      );
    },
    date() {
      return this.store.state.topBar.date;
    },
    warehouse() {
      return this.store.state.topBar.warehouse;
    },
    availablePanels() {
      const usedIds = this.panels.map((p) => p.id);

      return Object.values(this.possiblePanels).filter(
        (p) => !usedIds.includes(p.id)
      );
    },
  },
  mounted() {
    this.loadData();
    this.$nextTick(this.calculateHeights);
  },
  watch: {
    dashboard() {
      this.visible = this.dashboard.roles;
      this.loadDashboard();
    },
    editorMode() {
      this.selectedId = null;
      this.dirty = false;
      if (this.editorMode) {
        http
          .fetch("/dashboard/1")
          .then((data) => {
            this.allDashboards = data;
            this.allDashboards.push({
              id: null,
              name: this.$t("dashboardBlocks.editor.newViewName"),
              isNew: true,
              blocks: [],
              roles: [0],
            });
            const current = this.allDashboards.find(
              (d) => d.id === this.dashboard?.id
            );
            if (current) this.dashboard = current;
          })
          .catch(() => {
            this.allDashboards = [];
          });
        http
          .fetch("/users/roles")
          .then((data) => {
            this.roles = data;
          })
          .catch(() => {
            this.roles = [];
          });
      }
    },
    date: {
      deep: true,
      handler() {
        this.loadData();
      },
    },
    warehouse() {
      this.loadData();
    },
  },
  methods: {
    percent(val) {
      return Math.round(val * 10000) / 100;
    },
    loadData() {
      http
        .fetch(
          `/home?1${this.warehouse ? "&warehouse=" + this.warehouse : ""}${
            (this.date[0]
              ? "&from=" + this.date[0].toISOString().split("T")[0]
              : "") +
            (this.date[1]
              ? "&to=" + this.date[1].toISOString().split("T")[0]
              : "")
          }`
        )
        .then(async (data) => {
          this.total = data.total;
          this.appData = data.apps;
          this.payments = data.payments;

          let labels = [];
          let revenue = [];
          let sales = [];
          let avg = [];
          for (const date in data.total.daily) {
            labels.push(date);
            revenue.push(Number.parseFloat(data.total.daily[date].total));
            sales.push(Number.parseFloat(data.total.daily[date].count));
            avg.push(Number.parseFloat(data.total.daily[date].avg));
          }
          this.revenueByDays.labels = labels;
          this.revenueByDays.datasets[0].data = revenue;
          this.salesByDays.labels = labels;
          this.salesByDays.datasets[0].data = sales;
          this.avgByDays.labels = labels;
          this.avgByDays.datasets[0].data = avg;
          this.dashboards = data.dashboards;
          this.dashboard = data.dashboards[0];
          this.isAdmin = data.is_admin;
          await this.loadPanels();
          if (this.firstLoad) {
            this.firstLoad = false;
          }
        });
    },
    gridStyle(panel) {
      return {
        gridColumn: `span ${panel.size || 6}`,
        gridRow: `span ${panel.rows || 20}`,
      };
    },
    panelClass(panel) {
      return panel.size ? "col-" + panel.size : "col-6";
    },
    calculateHeights() {
      console.log(document.querySelectorAll(".dashboard-item"));
      document.querySelectorAll(".dashboard-item").forEach((el, index) => {
        const height = el.querySelector(".white-box").offsetHeight;
        const diff = height / 15;
        let rowSpan = Math.ceil(diff);
        const gap = rowSpan - diff;
        if (gap < 0.7) {
          rowSpan++;
        }
        console.log(height, rowSpan);
        this.panels[index].rows = rowSpan;
      });
    },

    panelLabel(panel) {
      if (panel.custom) return panel.name;
      const key = `dashboardBlocks.panels.${panel.id}`;
      return this.$te(key)
        ? this.$t(key)
        : panel.title || panel.name || panel.id;
    },
    setSize(panel, size) {
      panel.size = Math.min(12, Math.max(1, size));
      this.dirty = true;
      this.$nextTick(this.calculateHeights);
    },
    startResize(event, panel) {
      const grid = event.target.closest(".dashboard-grid");
      const gap = parseFloat(getComputedStyle(grid).columnGap) || 16;
      const unit = (grid.clientWidth + gap) / 12;
      const startX = event.clientX;
      const startSize = panel.size || 6;
      const move = (e) => {
        panel.size = Math.min(
          12,
          Math.max(1, startSize + Math.round((e.clientX - startX) / unit))
        );
      };
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        this.dirty = true;
        this.$nextTick(this.calculateHeights);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    },
    exitEditor() {
      if (this.dirty) {
        this.$refs.discardModal.showModal();
      } else {
        this.editorMode = false;
      }
    },
    discard() {
      this.editorMode = false;
      this.loadData();
    },
    changeSize(panel, delta) {
      panel.size = Math.min(12, Math.max(1, (panel.size || 6) + delta));
    },
    saveLayout() {
      let data = {
        id: this.dashboard.id ?? null,
        name: this.dashboard.name,
        blocks: [],
        visibility: this.visible,
      };
      for (const panel of this.panels) {
        data.blocks.push({
          block_name: panel.id,
          size: panel.size ?? 1,
          rows: panel.rows ?? 1,
        });
      }
      this.saving = true;
      http
        .fetch("/dashboard/1", data)
        .then(() => {
          this.editorMode = false;
          this.loadData();
        })
        .catch((err) => console.error(err))
        .finally(() => {
          this.saving = false;
        });
    },
    changeVisible(visible) {
      this.dirty = true;
      if (visible > 0) {
        this.visible = this.visible.filter((e) => e > 0);
      } else if (visible === 0) {
        this.visible = [0];
      }
    },
    addPanel(panel) {
      this.dirty = true;
      this.selectedId = panel.id;
      this.panels.push({
        ...panel,
        size: panel.size ?? 6,
        rows: panel.rows ?? 20,
      });

      this.$nextTick(this.calculateHeights);
    },
    removePanel(panel) {
      this.dirty = true;
      this.selectedId = null;
      this.panels = this.panels.filter((p) => p.id !== panel.id);
    },
    loadDashboard() {
      this.panels = [];
      for (const block of this.dashboard.blocks) {
        if (!this.possiblePanels[block.block_name]) {
          continue;
        }
        if (block.size) {
          this.possiblePanels[block.block_name].size = block.size;
        }
        if (block.rows) {
          this.possiblePanels[block.block_name].rows = block.rows;
        }
        this.panels.push(this.possiblePanels[block.block_name]);
      }
      setTimeout(() => {
        this.calculateHeights();
      }, 1000);
    },
    customPanel(block) {
      const id = `custom:${block.id}`;
      this.possiblePanels[id] = {
        id,
        app: null,
        component: defineAsyncComponent(() =>
          import(`../../components/home/CustomBlock.vue`)
        ),
        custom: true,
        data: { block },
        name: block.name,
        rows: 20,
        size: 12,
        title: block.name,
      };
      return this.possiblePanels[id];
    },
    blockSaved({ block, size, edited }) {
      if (!edited) {
        this.addPanel({ ...this.customPanel(block), size });
        return;
      }
      const old = this.possiblePanels[`custom:${block.id}`];
      const panel = {
        ...this.customPanel(block),
        size: old.size,
        rows: old.rows,
      };
      this.possiblePanels[panel.id] = panel;
      this.panels = this.panels.map((p) => (p.id === panel.id ? panel : p));
      this.key++;
    },
    askDeleteBlock(block) {
      this.blockToDelete = block;
      this.$refs.deleteBlockModal.showModal();
    },
    deleteBlock() {
      const id = `custom:${this.blockToDelete.id}`;
      http
        .fetch(
          `/dashboard/custom-blocks/${this.blockToDelete.id}`,
          {},
          false,
          "DELETE"
        )
        .then(() => {
          delete this.possiblePanels[id];
          this.panels = this.panels.filter((p) => p.id !== id);
        })
        .catch(() => {});
    },
    async loadPanels() {
      this.possiblePanels["Sales"] = {
        id: "Sales",
        app: null,
        component: defineAsyncComponent(() =>
          import(`../../components/home/Sales.vue`)
        ),
        data: {
          optionsNoText: this.optionsNoText,
          salesByDays: this.salesByDays,
          total: this.total,
        },
        name: "FulfillmentOrderCurrent",
        rows: 12,
        size: 4,
        title: "Sales number",
      };

      this.possiblePanels["Income"] = {
        id: "Income",
        app: null,
        component: defineAsyncComponent(() =>
          import(`../../components/home/Income.vue`)
        ),
        data: {
          optionsNoText: this.optionsNoText,
          revenueByDays: this.revenueByDays,
          total: this.total,
        },
        class: "white-box-big",
        name: "FulfillmentOrderCurrent",
        rows: 24,
        size: 5,
        title: "Sales value",
      };

      this.possiblePanels["Orders"] = {
        id: "Orders",
        app: null,
        component: defineAsyncComponent(() =>
          import(`../../components/home/Orders.vue`)
        ),
        data: {
          total: this.total,
        },
        name: "Orders number",
        rows: 12,
        size: 3,
        title: "Sales",
      };

      this.possiblePanels["AvgBasket"] = {
        id: "AvgBasket",
        app: null,
        component: defineAsyncComponent(() =>
          import(`../../components/home/AvgBasket.vue`)
        ),
        data: {
          optionsNoText: this.optionsNoText,
          avgByDays: this.avgByDays,
          total: this.total,
        },
        name: "Averige basket value",
        rows: 12,
        size: 4,
        title: "Sales",
      };

      this.possiblePanels["SuccessRate"] = {
        id: "SuccessRate",
        app: null,
        component: defineAsyncComponent(() =>
          import(`../../components/home/SuccessRate.vue`)
        ),
        data: {
          total: this.total,
        },
        name: "Payment success rate",
        rows: 12,
        size: 3,
        title: "Sales",
      };

      this.possiblePanels["PaymentsErrors"] = {
        id: "PaymentsErrors",
        app: null,
        component: defineAsyncComponent(() =>
          import(`../../components/home/PaymentsErrors.vue`)
        ),
        data: {
          total: this.total,
          payments: this.payments,
        },
        name: "Payment errors",
        rows: 12,
        size: 3,
        title: "Sales",
      };

      if (this.firstLoad) {
        const customBlocks = await http
          .fetch("/dashboard/custom-blocks")
          .catch(() => []);
        for (const block of customBlocks) {
          this.customPanel(block);
        }
      }

      let _this = this;
      const apps = this.store.state.apps;
      const loaders = [];

      for (const key in apps) {
        if (!apps[key].homeComponents) continue;

        for (const compKey in apps[key].homeComponents) {
          const comp = apps[key].homeComponents[compKey];

          const panelId = `${apps[key].name}_${comp.name}`;

          comp.app = apps[key].name;
          comp.data = this.appData[apps[key].id];
          comp.id = panelId;

          if (this.firstLoad) {
            loaders.push(
              externalComponent(
                `/${apps[key].name}/home/${comp.name}.umd.min.js`
              ).then((MyComponent) => {
                comp.component = MyComponent;
                _this.possiblePanels[panelId] = comp;
              })
            );
          } else {
            // already loaded
            this.possiblePanels[panelId] = comp;
          }
        }
      }

      // wait for ALL components
      if (loaders.length) {
        await Promise.all(loaders);
      }
      this.loadDashboard();
    },
  },
};
</script>
<style scoped>
.editor-mode .white-box-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.editor-mode .white-box {
  user-select: none;
}
.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-auto-rows: 15px;
  grid-auto-flow: dense;
  gap: 0 1rem;
}

.dashboard-item {
  position: relative;
  display: block;
}
.editor-mode .dashboard-grid {
  padding-top: 3.5rem;
}
.editor-mode .dashboard-item > .white-box {
  cursor: pointer;
  transition: box-shadow 0.15s;
}
.editor-mode .dashboard-item > .white-box::after {
  content: "";
  position: absolute;
  inset: -0.25rem;
  z-index: 6;
  border: 2px solid transparent;
  border-radius: 0.75rem;
  pointer-events: none;
  transition: border-color 0.15s;
}
.editor-mode .dashboard-item:not(.selected) > .white-box:hover {
  box-shadow: 0 0.25rem 1rem rgba(113, 58, 232, 0.12);
}
.editor-mode .dashboard-item:not(.selected) > .white-box:hover::after {
  border-color: rgba(113, 58, 232, 0.4);
}
.editor-mode .dashboard-item.selected > .white-box::after {
  border-color: var(--pf-accent, #713ae8);
}
.editor-bar {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.75rem 1rem;
  overflow-x: auto;
  white-space: nowrap;
  padding: 0.75rem 1rem;
  background: var(--pf-accent-soft, #efe8fd);
  border-radius: 0.75rem;
}
.editor-bar-title {
  font-size: 0.8125rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: var(--pf-accent-dark, #5b2bc4);
}
.editor-bar-name {
  flex: 0 1 12rem;
  min-width: 6rem;
}
.editor-bar > * {
  flex-shrink: 0;
}
.editor-bar > .editor-bar-name {
  flex-shrink: 1;
}
.editor-bar-dirty {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--pf-accent-dark, #5b2bc4);
}
.block-toolbar {
  position: absolute;
  top: -3.25rem;
  left: 0;
  z-index: 20;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.75rem;
  width: max-content;
  white-space: nowrap;
  padding: 0.25rem 0.75rem;
  color: #fff;
  background: #1e1b2e;
  border-radius: 0.625rem;
  box-shadow: 0 0.5rem 1.5rem rgba(30, 27, 46, 0.25);
}
.block-toolbar-label {
  font-size: 0.75rem;
  font-weight: 700;
  opacity: 0.8;
}
.block-toolbar-sizes {
  display: flex;
  gap: 0.125rem;
}
.block-toolbar button {
  min-height: 2.75rem;
  padding: 0.25rem 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #fff;
  background: transparent;
  border: 0;
  border-radius: 0.375rem;
}
.block-toolbar button.active {
  background: var(--pf-accent, #713ae8);
}
.block-toolbar .block-toolbar-remove {
  color: #ffb3ba;
}
.block-toolbar-separator {
  width: 1px;
  height: 1.25rem;
  background: rgba(255, 255, 255, 0.3);
}
.resize-handle {
  position: absolute;
  right: -0.5rem;
  bottom: -0.5rem;
  z-index: 5;
  width: 1rem;
  height: 1rem;
  background: var(--pf-accent, #713ae8);
  border-radius: 0.25rem;
  cursor: nwse-resize;
  touch-action: none;
}
.editor-dock {
  position: sticky;
  bottom: 0.75rem;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding: 0.75rem 1.25rem;
  background: #fff;
  border: 1px solid var(--pf-border, #e4e1f0);
  border-radius: 0.75rem;
  box-shadow: 0 0.5rem 1.5rem rgba(30, 27, 46, 0.12);
}
.editor-dock-title {
  font-size: 0.9375rem;
  white-space: nowrap;
}
.editor-dock-cards {
  display: flex;
  flex: 1;
  gap: 0.75rem;
  padding: 0.25rem;
  overflow-x: auto;
}
.dock-card {
  display: flex;
  flex: 0 0 11rem;
  flex-direction: column;
  justify-content: space-between;
  min-height: 4.5rem;
  padding: 0.625rem 0.75rem;
  font-family: inherit;
  color: var(--pf-text, #1e1b2e);
  text-align: left;
  background: #fff;
  border: 1px solid var(--pf-border, #e4e1f0);
  border-radius: 0.75rem;
}
.dock-card-new {
  align-items: flex-start;
  gap: 0.25rem;
  color: var(--pf-accent, #713ae8);
  background: #f7f3ff;
  border: 2px dashed #b79bff;
}
.dock-card-new strong {
  font-size: 0.875rem;
  color: var(--pf-text, #1e1b2e);
}
.dock-card-name {
  font-size: 0.875rem;
  font-weight: 700;
}
.dock-card-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.75rem;
  color: var(--pf-muted, #6b6880);
}
.dock-card-plus {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  font-size: 1rem;
  color: #fff;
  background: var(--pf-accent, #713ae8);
  border-radius: 0.375rem;
}
.drag-handle {
  cursor: grab;
  font-size: 1.2rem;
  padding: 0.4rem 0.4rem 0 0.4rem;
}

.resize-controls {
  margin-top: 0.4rem;
  margin-right: 0.4rem;
}
</style>
